/**
 * OpenSeadragon counterpart of BaseSlideMap.js, with the same props.
 *
 * Zoom is the OpenLayers zoom index (0 = coarsest stitched level), but fractional: wheel, pinch,
 * buttons and double-click are continuous. Positions in Redux and in the OL stand-ins on the viewer
 * use OL coordinates (image pixels, y up from the bottom) so other apps and split-sync keep working.
 */
import React, {Component} from 'react';
import OpenSeadragon from 'openseadragon';
import './styles.css';
import {store} from '../../../helper/store';
import {updateDisplaySetting, updateMapState} from '../../../redux_actions/maps.state.action';
import {isCtrlPermitted, isPermitted, isShiftPermitted} from '../../../utils/listeners';
import {CLICK_ZOOM_FACTOR, DEFAULT_MICRONS_PER_PIXEL, NAVIGATOR_LAYOUT, NAVIGATOR_SYNC_DELAYS_MS, SCALE_BAR} from './viewerConfig';
import {
    createTileSourceFromSlide,
    getBackgroundColor,
    getImageDimensions,
    getTiledOverlayConfig,
    getTiledZoom,
    morphleToOsdLevel,
} from './utils/tileSourceUtils';
import {getMappedZoomScale, getOsdViewerConfig, getSensitivity, getServedLevelCap, getTunedOsdConfig} from './osdSettings';
import {getPresetKey, getZoomPresets, nearestZoomIndex} from './utils/zoomPresets';
import {colorArrayToRGB, convertSettingsToFilter} from '../../../utils/utils';
import {componentIds, defaultRotationDegree, MorphleLogo, responsiveLayers} from '../../../utils/const';
import {IconButton, Paper, Tooltip} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import SlideName from '../headbar_apps/SlideName';
import BuildManifestButton from './BuildManifestButton';
import SeenAreaControls from './seenArea/SeenAreaControls';
import {SeenAreaController} from './seenArea/SeenAreaController';
import {isSeenAreaKey} from './seenArea/holdToggle';
import {levelKey, STITCHED, TILED} from './seenArea/seenAreaFormat';
import {getMaxViewZoom, getZoomLevels, isImportedSlide} from '../utils/map_utils';
import {patch} from '../../../helper/request';
import {error_manager} from '../../../helper/error_manager';
import {isOwner} from '../utils/displaySettingsUtils';
import {aclPermissions, hasPermissions} from '../../../const/ACLPermissions';

/** OL keeps at most 8 levels reachable (BaseSlideMap minZoom). */
const MAX_VISIBLE_LEVELS = 8;
const ROTATION_SAVE_DELAY_MS = 5000;

/** Map degrees to [0, 360) so Redux, the API and OSD share one continuous range. */
const normalizeRotationDeg0360 = (deg) => {
    if (typeof deg !== 'number' || Number.isNaN(deg)) return 0;
    return ((deg % 360) + 360) % 360;
};

class BaseSlideMapOSD extends Component {
    constructor(props) {
        super(props);
        this.viewer = null;
        this.viewerRef = React.createRef();
        // Grip positions are relative to this outer wrapper.
        this.viewerShellRef = React.createRef();
        this.state = {
            showNavigator: true,
            navigatorSize: null,
            navigatorResizeHandle: null,
            seenArea: null,
        };
        this.isMobile = document.documentElement.clientWidth <= responsiveLayers.MOBILE_WIDTH_LEVEL &&
            (document.documentElement.clientWidth < document.documentElement.clientHeight);
        this._pinchGestureActive = false;
        this._maxZoomIndex = null;
        this._scaleText = null;
        this._scaleBarWidth = null;
    }

    isPinchGestureActive = () => this._pinchGestureActive === true;

    getOsdViewerConfig = () => this.props.osdViewerConfig || getOsdViewerConfig();

    /** OSD_CONFIG with organisation tuning; read on every use so changes apply live. */
    getConfig = () => getTunedOsdConfig(this.getOsdViewerConfig());

    getSensitivity = () => getSensitivity(this.getOsdViewerConfig());

    getServedLevelCap = () => getServedLevelCap(this.props.slide, getZoomLevels(this.props.slide), this.getOsdViewerConfig());

    isActiveMap = () => store.getState().gammaStateReducer?.activeMapId === this.props.id;

    componentDidMount() {
        this.initViewer();
        document.addEventListener('keydown', this.keyDownListener, false);
        document.addEventListener('keyup', this.keyUpListener, false);
        window.addEventListener('blur', this.onWindowBlur);
    }

    componentWillUnmount() {
        this._unmounting = true;
        document.removeEventListener('keydown', this.keyDownListener, false);
        document.removeEventListener('keyup', this.keyUpListener, false);
        window.removeEventListener('blur', this.onWindowBlur);
        this._detachNavigatorResizeHandle();
        clearTimeout(this._rotationSaveTimeout);
        if (this._continuousZoomUiRaf != null) {
            cancelAnimationFrame(this._continuousZoomUiRaf);
            this._continuousZoomUiRaf = null;
        }
        if (this._wheelZoomEaseRaf != null) {
            cancelAnimationFrame(this._wheelZoomEaseRaf);
            this._wheelZoomEaseRaf = null;
        }
        if (this._prefetchRaf != null) {
            cancelAnimationFrame(this._prefetchRaf);
            this._prefetchRaf = null;
        }
        this._wheelZoomTarget = null;
        this._wheelZoomPivot = null;
        this.destroyViewer();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.displaySetting?.rotation !== this.props.displaySetting?.rotation) {
            this.updateRotation();
        }
        if (prevProps.digitalZoomStatus !== this.props.digitalZoomStatus) {
            this.applyDigitalZoomLimits();
            this.seenArea?.refreshScore();
        }
        const prevSlide = prevProps.slide;
        const slide = this.props.slide;
        if (prevSlide?.id === slide?.id &&
            (prevSlide?.txty !== slide?.txty || prevSlide?.stitchManifest !== slide?.stitchManifest)) {
            this.refreshTileExistence();
            this.seenArea?.refreshScore();
            this.seenArea?.scheduleDraw();
        }
        // The on-demand manifest build writes the seen-area tissue file too.
        if (prevSlide?.id === slide?.id && !prevSlide?.stitchManifest && slide?.stitchManifest) {
            this.seenArea?.session?.loadTissue(true);
        }
        if (prevProps.osdViewerConfig?.organisation !== this.props.osdViewerConfig?.organisation) {
            this.onOrganisationConfigChange();
        }
    }

    /** Mapping, tuning and digital-zoom mode come from the organisation config and may change mid-session. */
    onOrganisationConfigChange = () => {
        if (!this.viewer || !this.props.slide) return;
        this.updateState({zoomScale: getMappedZoomScale(this.props.slide, getZoomLevels(this.props.slide), this.getOsdViewerConfig())});
        this.applyFinerLodPreference();
        if (this._servedLevelCap !== this.getServedLevelCap()) {
            this._servedLevelCap = this.getServedLevelCap();
            this.refreshTileExistence();
        }
        this.seenArea?.refreshScore();
    };

    /**
     * Re-ask the tile source which tiles exist. Clearing `tilesMatrix` (OSD 4.1 internal) drops the
     * remembered URLs; already downloaded images are reused from the tile cache, so nothing flashes.
     */
    refreshTileExistence = () => {
        const tiledImage = this.viewer?.world?.getItemAt(0);
        if (!tiledImage) return;
        tiledImage.tilesMatrix = {};
        this.viewer.forceRedraw();
    };

    destroyViewer = () => {
        if (!this.viewer) return;
        this.stopSeenArea();
        if (this._boundWheelZoom && this.viewer.container) {
            this.viewer.container.removeEventListener('wheel', this._boundWheelZoom);
        }
        this.viewer.destroy();
        this.viewer = null;
    };

    initViewer = () => {
        const {slide} = this.props;
        if (!slide) return;

        const zoomLevels = getZoomLevels(slide);
        this._servedLevelCap = this.getServedLevelCap();

        // Container id must match the div rendered below.
        this.viewer = OpenSeadragon({
            id: `slide-map-${this.props.id}`,
            tileSources: createTileSourceFromSlide(slide, () => this.props.slide, () => this._servedLevelCap),
            ...this.getConfig(),
            backgroundColor: getBackgroundColor(slide),
            prefixUrl: '',
            constrainDuringPan: false,
            // Looser on mobile so pinch zoom-out is not undone by applyConstraints
            visibilityRatio: this.isMobile ? 0 : 0.5,
            compositeOperation: 'source-over',
        });

        this.viewer.addHandler('open', () => this.onViewerOpen());
        this.viewer.addHandler('animation-finish', this.onAnimationFinish);
        this.viewer.addHandler('resize', this.onViewerResize);
        this.viewer.addHandler('canvas-click', (event) => {
            if (this.props.onClick) this.props.onClick(event);
        });
        this.viewer.addHandler('canvas-double-click', (event) => {
            event.preventDefaultAction = true;
            const refPoint = this.viewer.viewport.pointFromPixel(event.position);
            this.setZoomLevel(this.getZoomLevel() + Math.log2(CLICK_ZOOM_FACTOR), false, refPoint);
        });
        // Maps-style unified pinch: pan + zoom + rotate in one transform
        this.viewer.addHandler('canvas-pinch', this.handleCanvasPinch);
        this.viewer.addHandler('canvas-release', this.handleCanvasRelease);
        this.viewer.addHandler('canvas-drag', this.handleCanvasDrag);
        this.viewer.addHandler('canvas-drag-end', this.handleCanvasDragEnd);

        this.addCompatibilityMethods();

        this._boundWheelZoom = this.handleWheel.bind(this);
        this.viewer.container.addEventListener('wheel', this._boundWheelZoom, {passive: false});

        this.updateState({
            slidemap: this.viewer,
            zoomLevels,
            zoomScale: getMappedZoomScale(slide, zoomLevels, this.getOsdViewerConfig()),
            nativeTopLevel: isImportedSlide(slide),
        });
    };

    // ------------------------------------------------------------------ coordinates

    getImageHeight = () => getImageDimensions(this.props.slide).height;

    /** OL coordinates (y up from the bottom) → OSD viewport point. */
    olToViewportPoint = (x, y) => this.viewer.viewport.imageToViewportCoordinates(x, this.getImageHeight() - y);

    /** OSD viewport point → OL coordinates. */
    viewportPointToOl = (point) => {
        const image = this.viewer.viewport.viewportToImageCoordinates(point);
        return [image.x, this.getImageHeight() - image.y];
    };

    // ------------------------------------------------------------------ zoom

    getZoomBounds = () => {
        const {slide, digitalZoomStatus} = this.props;
        const zoomLevels = getZoomLevels(slide);
        const topLevel = Math.max(0, zoomLevels.length - 1);
        const min = Math.max(0, zoomLevels.length - MAX_VISIBLE_LEVELS);
        const max = this._maxZoomIndex ?? getMaxViewZoom(slide, zoomLevels, digitalZoomStatus);
        return {topLevel, min, max};
    };

    /** OSD zoom at which the given level (top physical level = 1 image px per screen px) is shown. */
    osdZoomForLevel = (level) => {
        const {width} = getImageDimensions(this.props.slide);
        const containerWidth = this.viewer.container.clientWidth || 1;
        return Math.pow(2, level - this.getZoomBounds().topLevel) * width / containerWidth;
    };

    getRawZoomLevel = () => {
        const {width} = getImageDimensions(this.props.slide);
        const containerWidth = this.viewer.container.clientWidth || 1;
        const osdZoom = this.viewer.viewport.getZoom(true);
        return this.getZoomBounds().topLevel + Math.log2(osdZoom * containerWidth / width);
    };

    /** Current zoom as a fractional OL zoom index, clamped to the reachable range. */
    getZoomLevel = () => {
        if (!this.viewer?.viewport || !this.viewer.container) return 0;
        const {min, max} = this.getZoomBounds();
        return Math.max(min, Math.min(max, this.getRawZoomLevel()));
    };

    /** refPoint (OSD viewport point) stays fixed on screen; null zooms about the centre. */
    setZoomLevel = (level, immediately = false, refPoint = null) => {
        if (!this.viewer?.viewport || !this.viewer.container) return;
        if (!Number.isFinite(level)) return;
        const {min, max} = this.getZoomBounds();
        const clamped = Math.max(min, Math.min(level, max));
        this.viewer.viewport.zoomTo(this.osdZoomForLevel(clamped), refPoint, immediately);
    };

    getMinZoom = () => this.getZoomBounds().min;

    getMaxZoom = () => this.getZoomBounds().max;

    /** OSD reads these on the viewport, not the viewer, once it is open. */
    applyOsdZoomBounds = () => {
        if (!this.viewer?.viewport || !this.props.slide) return;
        this.viewer.viewport.maxZoomLevel = this.osdZoomForLevel(this.getZoomBounds().max);
    };

    /** Sync limits when digital zoom is toggled and clamp if the view is now above the maximum. */
    applyDigitalZoomLimits = () => {
        if (!this.viewer?.viewport || !this.props.slide) return;
        this._maxZoomIndex = null;
        this.applyOsdZoomBounds();
        const {max} = this.getZoomBounds();
        if (this.getRawZoomLevel() > max) this.setZoomLevel(max, true);
        this.updateMapState();
        this.updateScaleBar();
    };

    onViewerResize = () => {
        this.applyOsdZoomBounds();
        this.updateScaleBar();
    };

    // ------------------------------------------------------------------ gestures

    handleCanvasPinch = (event) => {
        if (!this.viewer?.viewport) return;
        if (!event.gesturePoints || event.gesturePoints.length < 2) return;
        if (!event.lastDistance || event.lastDistance <= 0) return;

        event.preventDefaultPanAction = true;
        event.preventDefaultZoomAction = true;
        event.preventDefaultRotateAction = true;

        this._pinchGestureActive = true;
        this.viewer._pinchGestureActive = true;

        const vp = this.viewer.viewport;
        const gp = event.gesturePoints;
        const centerPt = vp.pointFromPixel(event.center, true);
        const lastCenterPt = vp.pointFromPixel(event.lastCenter, true);

        // Keep content anchored under the moving pinch midpoint
        vp.panBy(lastCenterPt.minus(centerPt), true);

        const scale = event.distance / event.lastDistance;
        if (scale > 0 && Number.isFinite(scale) && scale !== 1) {
            vp.zoomBy(scale, centerPt, true);
        }

        const angle = Math.atan2(gp[1].currentPos.y - gp[0].currentPos.y, gp[1].currentPos.x - gp[0].currentPos.x);
        const lastAngle = Math.atan2(gp[1].lastPos.y - gp[0].lastPos.y, gp[1].lastPos.x - gp[0].lastPos.x);
        const rotDeltaDeg = (angle - lastAngle) * (180 / Math.PI);
        if (Math.abs(rotDeltaDeg) > 1e-6) {
            vp.rotateTo(vp.getRotation(true) + rotDeltaDeg, centerPt, true);
        }

        // Clamp zoom only; full applyConstraints (visibilityRatio) would block zoom-out mid-gesture
        this.applyPinchZoomClamp(vp);
        this.scheduleContinuousZoomUiSync();
    };

    applyPinchZoomClamp = (vp) => {
        const actualZoom = vp.getZoom(true);
        const constrained = Math.max(vp.getMinZoom(), Math.min(vp.getMaxZoom(), actualZoom));
        if (actualZoom !== constrained) {
            vp.zoomTo(constrained, vp.zoomPoint, true);
        }
    };

    handleCanvasRelease = (event) => {
        if (!this._pinchGestureActive) return;
        const touchCount = event.originalEvent?.touches?.length ?? 0;
        if (touchCount >= 2) return;
        this.finishPinchGesture();
    };

    finishPinchGesture = () => {
        if (!this._pinchGestureActive) return;
        this._pinchGestureActive = false;
        if (this.viewer) this.viewer._pinchGestureActive = false;
        if (this.viewer?.viewport) this.viewer.viewport.applyConstraints(true);
        this.updateMapState();
        this.updateScaleBar();
        this.onPinchGestureEnd();
    };

    /** Hook for SlidemapOSD. */
    onPinchGestureEnd = () => {};

    /** OSD pans by the same `delta` object after this handler, so scaling it applies pan sensitivity. */
    handleCanvasDrag = (event) => {
        const {pan} = this.getSensitivity();
        if (pan === 1 || !event.delta) return;
        event.delta.x *= pan;
        event.delta.y *= pan;
    };

    /** OSD's flick reads its own copy of the speed, so the flick is redone here with the scaled speed. */
    handleCanvasDragEnd = (event) => {
        const {pan} = this.getSensitivity();
        const viewport = this.viewer?.viewport;
        if (pan === 1 || !viewport) return;
        const gestureSettings = this.viewer.gestureSettingsByDeviceType(event.pointerType);
        if (!gestureSettings.flickEnabled || event.speed < gestureSettings.flickMinSpeed) return;
        event.preventDefaultAction = true;
        const amplitude = gestureSettings.flickMomentum * event.speed * pan;
        const center = viewport.pixelFromPoint(viewport.getCenter(true));
        viewport.panTo(viewport.pointFromPixel(new OpenSeadragon.Point(
            center.x - amplitude * Math.cos(event.direction),
            center.y - amplitude * Math.sin(event.direction))), false);
        viewport.applyConstraints();
    };

    /**
     * Ctrl/Cmd+wheel rotates (saved to the display setting like OpenLayers); Shift+wheel is reserved
     * for Z-stack / exposure, which fall back to OpenLayers. Plain wheel accumulates scroll into a
     * target zoom and eases toward it each frame.
     */
    handleWheel(e) {
        if (!this.viewer?.viewport) return;
        e.preventDefault();
        e.stopPropagation();

        if (e.ctrlKey || e.metaKey) {
            if (isCtrlPermitted(e)) this.rotateBy(e.deltaY < 0 ? defaultRotationDegree : -defaultRotationDegree);
            return;
        }
        if (e.shiftKey && isShiftPermitted(e)) return;

        const viewport = this.viewer.viewport;
        this._wheelZoomPivot = viewport.pointFromPixel(new OpenSeadragon.Point(e.offsetX, e.offsetY));

        const config = this.getConfig();
        let delta = e.deltaY;
        if (e.deltaMode === 1) delta *= config.pixelsPerWheelLine;
        else if (e.deltaMode === 2) delta *= config.pixelsPerWheelLine * 16;
        if (!Number.isFinite(delta) || delta === 0) return;

        // Sigmoid compresses large mouse notches; trackpad deltas stay fine-grained.
        // Mouse vs trackpad is decided on the raw delta; sensitivity scales what goes into the sigmoid.
        const isCoarse = Math.abs(delta) > config.wheelCoarseThreshold;
        const absDelta = Math.abs(delta) * this.getSensitivity().wheel;
        const zoomRate = isCoarse ? config.wheelZoomRateCoarse : config.wheelZoomRate;
        const maxScale = isCoarse ? config.wheelZoomMaxScalePerFrameCoarse : config.wheelZoomMaxScalePerFrame;
        let scale = maxScale / (1 + Math.exp(-absDelta * zoomRate));
        scale = Math.max(1.001, Math.min(maxScale, scale));
        if (delta > 0) scale = 1 / scale;

        const fromZoom = typeof this._wheelZoomTarget === 'number' ? this._wheelZoomTarget : viewport.getZoom(true);
        this._wheelZoomTarget = Math.max(viewport.getMinZoom(), Math.min(viewport.getMaxZoom(), fromZoom * scale));
        this.ensureWheelZoomEaseLoop();
    }

    ensureWheelZoomEaseLoop = () => {
        if (this._wheelZoomEaseRaf != null) return;
        const step = () => {
            this._wheelZoomEaseRaf = null;
            const viewport = this.viewer?.viewport;
            if (!viewport || typeof this._wheelZoomTarget !== 'number') return;

            const current = viewport.getZoom(true);
            const target = this._wheelZoomTarget;
            const pivot = this._wheelZoomPivot || undefined;
            const settled = Math.abs(target - current) < this.getConfig().wheelZoomSettleEpsilon * Math.max(1, Math.abs(target));

            viewport.zoomTo(settled ? target : current + (target - current) * this.getConfig().wheelZoomSmoothing, pivot, true);
            this.applyPinchZoomClamp(viewport);
            this.scheduleContinuousZoomUiSync();
            if (settled) {
                this._wheelZoomTarget = null;
                return;
            }
            this._wheelZoomEaseRaf = requestAnimationFrame(step);
        };
        this._wheelZoomEaseRaf = requestAnimationFrame(step);
    };

    /** Throttle Redux/UI updates to one per frame while zoom is continuous. */
    scheduleContinuousZoomUiSync = () => {
        if (this._continuousZoomUiRaf != null) return;
        this._continuousZoomUiRaf = requestAnimationFrame(() => {
            this._continuousZoomUiRaf = null;
            this.updateMapState();
            this.updateScaleBar();
            this.scheduleNeighborLevelPrefetch();
        });
    };

    // ------------------------------------------------------------------ rotation

    rotateBy = (deltaDeg) => {
        const viewport = this.viewer.viewport;
        const rotation = normalizeRotationDeg0360((viewport.getRotation() || 0) + deltaDeg);
        // Immediate: an animated rotation leaves the field-tile overlay out of sync for several frames.
        viewport.setRotation(rotation, true);
        this.saveRotation(Math.round(rotation));
    };

    saveRotation = (rotation) => {
        const displaySetting = {rotation};
        store.dispatch(updateDisplaySetting(this.props.id, displaySetting));
        if (isOwner(this.props.displaySetting) && hasPermissions([aclPermissions.update_slide_setting])) {
            clearTimeout(this._rotationSaveTimeout);
            this._rotationSaveTimeout = setTimeout(() => {
                patch(`/api/display/settings/${this.props.displaySetting.id}/`, displaySetting).then()
                    .catch(error => error_manager(`Failed to update rotation`, error));
            }, ROTATION_SAVE_DELAY_MS);
        }
    };

    updateRotation = () => {
        if (!this.viewer?.viewport) return;
        const rotation = normalizeRotationDeg0360(this.props.displaySetting?.rotation || 0);
        if (rotation === normalizeRotationDeg0360(this.viewer.viewport.getRotation())) return;
        this.viewer.viewport.setRotation(rotation, true);
    };

    // ------------------------------------------------------------------ OpenLayers stand-ins

    /** Other apps call OL map/view methods on `slidemap`; these keep them working or no-op safely. */
    addCompatibilityMethods = () => {
        const viewer = this.viewer;
        const self = this;

        viewer.getLayers = () => ({
            getArray: () => [],
            forEach: () => {},
            getLength: () => 0,
        });

        viewer.getView = () => ({
            getCenter: () => viewer.viewport ? self.viewportPointToOl(viewer.viewport.getCenter(true)) : [0, 0],
            getZoom: () => self.getZoomLevel(),
            getMinZoom: () => self.getMinZoom(),
            getMaxZoom: () => self.getMaxZoom(),
            getRotation: () => (viewer.viewport?.getRotation() || 0) * (Math.PI / 180),
            // Image pixels per screen pixel, as in the OL 'pixels' projection
            getResolution: () => {
                if (!viewer.viewport) return 1;
                const {width} = getImageDimensions(self.props.slide);
                return width / (viewer.viewport.getZoom(true) * (viewer.container.clientWidth || 1));
            },
            getProjection: () => ({
                getExtent: () => {
                    const {width, height} = getImageDimensions(self.props.slide);
                    return [0, 0, width, height];
                },
                getCode: () => 'MORPHLE',
            }),
            // Uses all four rotated corners; the unrotated bounds miss parts of the screen after rotation.
            calculateExtent: () => {
                if (!viewer.viewport) return [0, 0, 100, 100];
                const height = self.getImageHeight();
                const bounds = viewer.viewport.getBounds(true);
                const item = viewer.world && viewer.world.getItemCount() > 0 ? viewer.world.getItemAt(0) : null;
                const corners = [bounds.getTopLeft(), bounds.getTopRight(), bounds.getBottomRight(), bounds.getBottomLeft()];
                let minIx = Infinity, maxIx = -Infinity, minIy = Infinity, maxIy = -Infinity;
                for (const c of corners) {
                    const p = item
                        ? item.viewportToImageCoordinates(c.x, c.y, true)
                        : viewer.viewport.viewportToImageCoordinates(c.x, c.y);
                    if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') continue;
                    minIx = Math.min(minIx, p.x);
                    maxIx = Math.max(maxIx, p.x);
                    minIy = Math.min(minIy, p.y);
                    maxIy = Math.max(maxIy, p.y);
                }
                if (![minIx, maxIx, minIy, maxIy].every(Number.isFinite)) return [0, 0, 100, 100];
                return [minIx, height - maxIy, maxIx, height - minIy];
            },
            setCenter: (center) => {
                if (!viewer.viewport) return;
                viewer.viewport.panTo(self.olToViewportPoint(center[0], center[1]));
            },
            setZoom: (zoom, immediately) => self.setZoomLevel(zoom, !!immediately),
            setMaxZoom: (maxZoom) => {
                self._maxZoomIndex = maxZoom;
                self.applyOsdZoomBounds();
            },
            setMinZoom: () => {},
            setRotation: (rotation) => {
                if (!viewer.viewport) return;
                viewer.viewport.setRotation(rotation * (180 / Math.PI), true);
            },
            // Accepts an OL extent or anything with getExtent() (OL geometries); no argument fits the slide.
            fit: (target) => {
                if (!viewer.viewport) return;
                const extent = Array.isArray(target) ? target : target?.getExtent?.();
                if (!extent || extent.length < 4 || !extent.every(Number.isFinite)) {
                    viewer.viewport.goHome();
                    return;
                }
                const [minX, minY, maxX, maxY] = extent;
                const topLeft = self.olToViewportPoint(minX, maxY);
                const bottomRight = self.olToViewportPoint(maxX, minY);
                viewer.viewport.fitBoundsWithConstraints(new OpenSeadragon.Rect(
                    topLeft.x, topLeft.y, Math.max(bottomRight.x - topLeft.x, 1e-9), Math.max(bottomRight.y - topLeft.y, 1e-9)));
            },
            animate: (options, callback) => {
                if (!viewer.viewport) return;
                if (options.center) viewer.viewport.panTo(self.olToViewportPoint(options.center[0], options.center[1]));
                if (options.rotation !== undefined) viewer.viewport.setRotation(options.rotation * (180 / Math.PI), true);
                if (options.zoom !== undefined) self.setZoomLevel(options.zoom, false);
                if (typeof callback === 'function') setTimeout(callback, Math.max(0, options.duration || 0));
            },
            on: () => {},
            un: () => {},
        });

        viewer.getSize = () => {
            const container = viewer.container;
            return container ? [container.clientWidth, container.clientHeight] : [800, 600];
        };

        viewer.get = (key) => (key === 'id' ? self.props.id : undefined);
        viewer.set = () => {};

        viewer.addEventListener = viewer.addHandler;
        viewer.removeEventListener = viewer.removeHandler;
        viewer.on = viewer.addHandler;
        viewer.un = viewer.removeHandler;

        viewer.getZoomLevel = () => self.getZoomLevel();
        viewer.setZoomLevel = (level, immediately = true) => self.setZoomLevel(level, immediately);
        viewer.getMinZoom = () => self.getMinZoom();
        viewer.getMaxZoom = () => self.getMaxZoom();
        viewer.applyDigitalZoomLimits = () => self.applyDigitalZoomLimits();

        // Current (not target) positions, so overlays stay aligned during animations.
        viewer.olToPixel = ([x, y]) => {
            if (!viewer.viewport) return [0, 0];
            const pixel = viewer.viewport.pixelFromPoint(self.olToViewportPoint(x, y), true);
            return [pixel.x, pixel.y];
        };
        viewer.pixelToOl = ([x, y]) => {
            if (!viewer.viewport) return [0, 0];
            return self.viewportPointToOl(viewer.viewport.pointFromPixel(new OpenSeadragon.Point(x, y), true));
        };

        viewer._isOpenSeadragon = true;
    };

    // ------------------------------------------------------------------ lifecycle

    /**
     * Opens fit-to-screen unless the map state (URL or a previous viewer) carries a position.
     * A prototype method (not an arrow field) so SlidemapOSD can extend it via super.
     */
    onViewerOpen() {
        const {x, y, z} = this.props;
        this.applyOsdZoomBounds();

        const hasPosition = x !== -1 && y !== -1 && x != null && y != null;
        if (hasPosition) {
            this.viewer.viewport.panTo(this.olToViewportPoint(x, y), true);
        }
        if (hasPosition || z > 0) {
            this.setZoomLevel(z, true);
        }

        this.updateRotation();
        this.applyFinerLodPreference();
        this.updateMapState();
        this.updateScaleBar();

        if (this.viewer.navigator) {
            // OSD pans to a navigator click with an animation; jump there instead.
            const navigatorJumpToClick = (event) => {
                const navVP = this.viewer?.navigator?.viewport;
                if (!this.viewer?.viewport || !navVP) return;
                this.viewer.viewport.panTo(navVP.pointFromPixel(event.position, true), true);
                this.scheduleNeighborLevelPrefetch();
            };
            this.viewer.navigator.addHandler('canvas-press', navigatorJumpToClick);
            this.viewer.navigator.addHandler('canvas-click', navigatorJumpToClick);
            this._attachNavigatorResizeHandle();
        }
        this.startSeenArea();
    }

    /**
     * Pushes level-of-detail and loader tuning onto the live viewer; the constructor options alone
     * are stale on the opened TiledImage and after an organisation tuning change.
     */
    applyFinerLodPreference = () => {
        const tiledImage = this.viewer?.world?.getItemCount?.() > 0 ? this.viewer.world.getItemAt(0) : null;
        if (!tiledImage) return;
        const config = this.getConfig();
        tiledImage.minPixelRatio = config.minPixelRatio;
        this.viewer.minPixelRatio = config.minPixelRatio;
        tiledImage.immediateRender = config.immediateRender !== false;
        if (this.viewer.imageLoader) this.viewer.imageLoader.jobLimit = config.imageLoaderLimit;
        if (this.viewer.tileCache) this.viewer.tileCache._maxImageCacheCount = config.maxImageCacheCount;
        this.viewer.forceRedraw();
        this.scheduleNeighborLevelPrefetch();
    };

    scheduleNeighborLevelPrefetch = () => {
        if (this._prefetchRaf != null) return;
        this._prefetchRaf = requestAnimationFrame(() => {
            this._prefetchRaf = null;
            this.prefetchNeighborLevelTiles();
        });
    };

    /**
     * Queues visible tiles of the neighbouring levels so zooming scales already-loaded tiles instead of
     * waiting. Finer levels come first with their own budget. Uses OSD 4.1 internals (`_getTile`,
     * `_loadTile`, `_getCornerTiles`, `_viewportToTiledImageRectangle`); the version is pinned for this.
     */
    prefetchNeighborLevelTiles = () => {
        const viewer = this.viewer;
        const slide = this.props.slide;
        if (!viewer?.viewport || !viewer.world || viewer.world.getItemCount() < 1 || !slide) return;
        const tiledImage = viewer.world.getItemAt(0);
        if (!tiledImage?.source || typeof tiledImage._getTile !== 'function' ||
            typeof tiledImage._loadTile !== 'function' || typeof tiledImage._getCornerTiles !== 'function' ||
            typeof tiledImage._viewportToTiledImageRectangle !== 'function') {
            return;
        }

        const config = this.getConfig();
        const {min, max} = this.getZoomBounds();
        const currentLevel = Math.round(this.getZoomLevel());
        const preferFiner = Math.max(0, config.preferFinerMorphleLevels ?? 0);
        const finerOffsets = Array.from({length: preferFiner}, (_, i) => i + 1);
        const offsets = [...finerOffsets, ...config.prefetchNeighborLevelOffsets.filter(o => !finerOffsets.includes(o))];
        const finerBudget = config.prefetchFinerMaxTilesPerTick;
        const maxTiles = finerBudget + config.prefetchMaxTilesPerTick;
        if (maxTiles <= 0) return;

        let drawArea;
        try {
            drawArea = tiledImage._viewportToTiledImageRectangle(viewer.viewport.getBoundsWithMargins(true));
            const clipped = tiledImage._viewportToTiledImageRectangle(tiledImage.getClippedBounds(true));
            drawArea = drawArea && clipped ? drawArea.intersection(clipped) : drawArea;
        } catch (e) {
            return;
        }
        if (!drawArea) return;

        const topLeft = drawArea.getBoundingBox().getTopLeft();
        const bottomRight = drawArea.getBoundingBox().getBottomRight();
        const now = OpenSeadragon.now();
        let queued = 0;
        let queuedFiner = 0;

        for (const offset of offsets) {
            if (queued >= maxTiles) break;
            const isFiner = offset > 0 && offset <= preferFiner;
            if (isFiner && queuedFiner >= finerBudget) continue;

            const level = currentLevel + offset;
            if (level < min || level > max) continue;
            const osdLevel = morphleToOsdLevel(slide, level);
            if (osdLevel == null || osdLevel < tiledImage.source.minLevel || osdLevel > tiledImage.source.maxLevel) continue;

            let corners;
            try {
                corners = tiledImage._getCornerTiles(osdLevel, topLeft, bottomRight);
            } catch (e) {
                continue;
            }
            if (!corners?.topLeft || !corners?.bottomRight) continue;
            const numTiles = tiledImage.source.getNumTiles(osdLevel);

            for (let x = corners.topLeft.x; x <= corners.bottomRight.x && queued < maxTiles; x++) {
                for (let y = corners.topLeft.y; y <= corners.bottomRight.y && queued < maxTiles; y++) {
                    if (isFiner && queuedFiner >= finerBudget) break;
                    try {
                        const tile = tiledImage._getTile(x, y, osdLevel, now, numTiles,
                            tiledImage._worldWidthCurrent, tiledImage._worldHeightCurrent);
                        // Tiles the source says are missing (manifest, tiled-overlay levels, virtual digital) have exists=false.
                        if (!tile || !tile.exists || tile.loaded || tile.loading || !tile.getUrl()) continue;
                        tiledImage._loadTile(tile, now);
                        queued += 1;
                        if (isFiner) queuedFiner += 1;
                    } catch (e) {
                        // loader back-pressure or a tile outside the grid
                    }
                }
            }
        }
    };

    /** Equivalent of OL moveend; Redux is only updated once the animation settles. */
    onAnimationFinish = () => {
        if (this.isPinchGestureActive()) return;
        this.updateMapState();
        this.updateScaleBar();
        this.scheduleNeighborLevelPrefetch();
    };

    updateMapState = () => {
        if (!this.viewer?.viewport) return;
        const [x, y] = this.viewportPointToOl(this.viewer.viewport.getCenter(true));
        this.updateState({
            x,
            y,
            z: this.getZoomLevel(),
            r: (this.viewer.viewport.getRotation() || 0) * (Math.PI / 180),
        });
    };

    updateState = (mapState) => store.dispatch(updateMapState(this.props.id, mapState));

    // ------------------------------------------------------------------ navigator resize

    _loadNavigatorSize = () => {
        try {
            const raw = localStorage.getItem(this.getConfig().navigatorResize.storageKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (typeof parsed?.width === 'number' && typeof parsed?.height === 'number') return parsed;
        } catch (e) { /* ignore */ }
        return null;
    };

    _saveNavigatorSize = (width, height) => {
        try {
            localStorage.setItem(this.getConfig().navigatorResize.storageKey, JSON.stringify({width, height}));
        } catch (e) { /* ignore */ }
    };

    _clampNavigatorSize = (width, height) => {
        const {minSize, maxRatio} = this.getConfig().navigatorResize;
        const container = this.viewer?.container;
        const maxW = container ? Math.max(minSize, Math.floor(container.clientWidth * maxRatio)) : 400;
        const maxH = container ? Math.max(minSize, Math.floor(container.clientHeight * maxRatio)) : 400;
        return {
            width: Math.round(Math.min(maxW, Math.max(minSize, width))),
            height: Math.round(Math.min(maxH, Math.max(minSize, height))),
        };
    };

    _applyNavigatorSize = (width, height, {syncReact = true} = {}) => {
        const nav = this.viewer?.navigator;
        const navEl = nav?.element;
        if (!navEl) return null;
        const size = this._clampNavigatorSize(width, height);

        // Stop OSD resizing the navigator with the viewer, then force the pixel size
        if (typeof nav.setWidth === 'function') {
            nav._resizeWithViewer = false;
            nav.width = size.width;
            nav.height = size.height;
        }
        navEl.style.setProperty('width', `${size.width}px`, 'important');
        navEl.style.setProperty('height', `${size.height}px`, 'important');
        if (typeof nav.updateSize === 'function') nav.updateSize();
        this.seenArea?.scheduleDraw();

        this._navigatorSizeCache = size;
        if (syncReact) {
            this.setState({navigatorSize: size}, () => this._syncNavigatorResizeHandlePosition());
        } else {
            this._syncNavigatorResizeHandlePosition({immediateDom: true, size});
        }
        return size;
    };

    _readNavigatorSize = () => {
        if (this._navigatorSizeCache) return this._navigatorSizeCache;
        const navEl = this.viewer?.navigator?.element;
        if (!navEl) return null;
        const width = navEl.offsetWidth || parseInt(navEl.style.width, 10) || 0;
        const height = navEl.offsetHeight || parseInt(navEl.style.height, 10) || 0;
        if (width < 8 || height < 8) return null;
        return {width, height};
    };

    /** immediateDom: move the grip element directly (during drag, avoids setState churn). */
    _syncNavigatorResizeHandlePosition = (opts = {}) => {
        const navEl = this.viewer?.navigator?.element;
        const shell = this.viewerShellRef?.current;
        const handleEl = this._navigatorResizeHandleEl;
        if (!navEl || !shell || !this.state.showNavigator || navEl.style.display === 'none') {
            if (this.state.navigatorResizeHandle) this.setState({navigatorResizeHandle: null});
            return;
        }
        const navRect = navEl.getBoundingClientRect();
        const shellRect = shell.getBoundingClientRect();
        if (navRect.width < 8 || navRect.height < 8) return;

        const next = {
            left: Math.round(navRect.left - shellRect.left),
            top: Math.round(navRect.bottom - shellRect.top - NAVIGATOR_LAYOUT.gripSize),
        };
        const size = opts.size || {width: Math.round(navRect.width), height: Math.round(navRect.height)};
        this._navigatorSizeCache = size;

        if (opts.immediateDom && handleEl) {
            handleEl.style.left = `${next.left}px`;
            handleEl.style.top = `${next.top}px`;
            return;
        }

        const prevHandle = this.state.navigatorResizeHandle;
        const prevSize = this.state.navigatorSize;
        const handleSame = prevHandle && prevHandle.left === next.left && prevHandle.top === next.top;
        const sizeSame = prevSize && prevSize.width === size.width && prevSize.height === size.height;
        if (handleSame && sizeSame) return;
        this.setState({navigatorResizeHandle: next, navigatorSize: size});
    };

    _ensureNavigatorHasPixelSize = () => {
        const navEl = this.viewer?.navigator?.element;
        if (!navEl) return;
        const parsedW = parseInt(navEl.style.width, 10);
        const parsedH = parseInt(navEl.style.height, 10);
        if (parsedW > 8 && parsedH > 8) {
            this._navigatorSizeCache = {width: parsedW, height: parsedH};
            return;
        }
        const cw = this.viewer.container?.clientWidth || 800;
        const ch = this.viewer.container?.clientHeight || 600;
        this._applyNavigatorSize(Math.round(cw * this.getConfig().navigatorSizeRatio),
            Math.round(ch * this.getConfig().navigatorSizeRatio), {syncReact: true});
    };

    _attachNavigatorResizeHandle = () => {
        if (this._navigatorResizeAttached) return;
        this._navigatorResizeAttached = true;

        this._ensureNavigatorHasPixelSize();
        const saved = this._loadNavigatorSize();
        if (saved) this._applyNavigatorSize(saved.width, saved.height);

        if (typeof ResizeObserver !== 'undefined' && this.viewer?.navigator?.element) {
            this._navigatorResizeObserver = new ResizeObserver(() => {
                if (this._navigatorResizeState) return;
                this._syncNavigatorResizeHandlePosition();
            });
            this._navigatorResizeObserver.observe(this.viewer.navigator.element);
            if (this.viewer.container) this._navigatorResizeObserver.observe(this.viewer.container);
        }
        this.viewer.addHandler('resize', this._syncNavigatorResizeHandlePosition);
        requestAnimationFrame(() => requestAnimationFrame(() => this._syncNavigatorResizeHandlePosition()));
        this._navigatorSyncTimers = NAVIGATOR_SYNC_DELAYS_MS.map(ms =>
            setTimeout(() => this._syncNavigatorResizeHandlePosition(), ms));
    };

    /** Grip position from the live navigator box, or from the cached size and the CSS offsets. */
    _getNavigatorResizeHandleStyle = () => {
        const {gripSize, top, right} = NAVIGATOR_LAYOUT;
        const shell = this.viewerShellRef?.current;
        const navEl = this.viewer?.navigator?.element;

        if (shell && navEl && navEl.style.display !== 'none') {
            const navRect = navEl.getBoundingClientRect();
            const shellRect = shell.getBoundingClientRect();
            if (navRect.width >= 8 && navRect.height >= 8) {
                return {
                    left: Math.round(navRect.left - shellRect.left),
                    top: Math.round(navRect.bottom - shellRect.top - gripSize),
                    width: gripSize,
                    height: gripSize,
                };
            }
        }

        const cw = shell?.clientWidth || this.viewer?.container?.clientWidth || 800;
        const ch = shell?.clientHeight || this.viewer?.container?.clientHeight || 600;
        const size = this._navigatorSizeCache || this.state.navigatorSize || {
            width: Math.round(cw * this.getConfig().navigatorSizeRatio),
            height: Math.round(ch * this.getConfig().navigatorSizeRatio),
        };
        return {
            right: right + size.width - gripSize,
            top: top + size.height - gripSize,
            width: gripSize,
            height: gripSize,
        };
    };

    _detachNavigatorResizeHandle = () => {
        document.removeEventListener('pointermove', this._onNavigatorResizeMove, true);
        document.removeEventListener('pointerup', this._onNavigatorResizeEnd, true);
        document.removeEventListener('pointercancel', this._onNavigatorResizeEnd, true);
        (this._navigatorSyncTimers || []).forEach(id => clearTimeout(id));
        this._navigatorSyncTimers = [];
        if (this.viewer) this.viewer.removeHandler('resize', this._syncNavigatorResizeHandlePosition);
        if (this._navigatorResizeObserver) {
            this._navigatorResizeObserver.disconnect();
            this._navigatorResizeObserver = null;
        }
        this._navigatorResizeAttached = false;
        this._navigatorResizeState = null;
        this._navigatorResizeHandleEl = null;
        document.body.style.cursor = '';
    };

    _onNavigatorResizeStart = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const size = this._readNavigatorSize() || this.state.navigatorSize;
        if (!size) return;

        this._navigatorResizeHandleEl = e.currentTarget;
        this._navigatorResizeState = {
            startX: e.clientX,
            startY: e.clientY,
            startW: size.width,
            startH: size.height,
            pointerId: e.pointerId,
        };
        try {
            e.currentTarget.setPointerCapture?.(e.pointerId);
        } catch (err) { /* ignore */ }

        document.body.style.cursor = 'nesw-resize';
        document.addEventListener('pointermove', this._onNavigatorResizeMove, true);
        document.addEventListener('pointerup', this._onNavigatorResizeEnd, true);
        document.addEventListener('pointercancel', this._onNavigatorResizeEnd, true);
    };

    _onNavigatorResizeMove = (e) => {
        if (!this._navigatorResizeState) return;
        e.preventDefault();
        e.stopPropagation();
        const {startX, startY, startW, startH} = this._navigatorResizeState;
        // Top-right navigator with the grip at its bottom-left: dragging left/down grows it
        this._applyNavigatorSize(startW + (startX - e.clientX), startH + (e.clientY - startY), {syncReact: false});
    };

    _onNavigatorResizeEnd = (e) => {
        if (!this._navigatorResizeState) return;
        e.preventDefault();
        e.stopPropagation();
        document.removeEventListener('pointermove', this._onNavigatorResizeMove, true);
        document.removeEventListener('pointerup', this._onNavigatorResizeEnd, true);
        document.removeEventListener('pointercancel', this._onNavigatorResizeEnd, true);
        document.body.style.cursor = '';
        try {
            this._navigatorResizeHandleEl?.releasePointerCapture?.(this._navigatorResizeState.pointerId);
        } catch (err) { /* ignore */ }

        const size = this._navigatorSizeCache || this._readNavigatorSize();
        if (size) {
            const clamped = this._applyNavigatorSize(size.width, size.height, {syncReact: true});
            if (clamped) this._saveNavigatorSize(clamped.width, clamped.height);
        }
        this._navigatorResizeState = null;
        this._navigatorResizeHandleEl = null;
    };

    toggleNavigator = () => {
        const next = !this.state.showNavigator;
        this.setState({showNavigator: next}, () => {
            if (this.viewer?.navigator?.element) {
                this.viewer.navigator.element.style.display = next ? '' : 'none';
            }
            this._syncNavigatorResizeHandlePosition();
            this.seenArea?.scheduleDraw();
        });
    };

    /** Nearest reachable stop to a shown magnification (labels after the organisation mapping). */
    zoomToMagnification = (targetMag) => {
        const {min, max} = this.getZoomBounds();
        const zoomScale = getMappedZoomScale(this.props.slide, getZoomLevels(this.props.slide), this.getOsdViewerConfig());
        const index = nearestZoomIndex(zoomScale, min, max, targetMag);
        if (index != null) this.setZoomLevel(index);
    };

    // ------------------------------------------------------------------ scale bar

    /** Picks a round length so the bar stays within SCALE_BAR widths and publishes it for ScaleBarOSD. */
    updateScaleBar = () => {
        if (!this.viewer?.viewport) return;
        const {slide} = this.props;
        const {width: imageWidth} = getImageDimensions(slide);
        const containerWidth = this.viewer.container.clientWidth;
        const screenPixelsPerImagePixel = (containerWidth / imageWidth) * this.viewer.viewport.getZoom(true);
        const micronsPerScreenPixel = (slide.uperpixel || DEFAULT_MICRONS_PER_PIXEL) / screenPixelsPerImagePixel;

        let niceValue = SCALE_BAR.niceValuesMicrons[0];
        for (const v of SCALE_BAR.niceValuesMicrons) {
            const barWidth = v / micronsPerScreenPixel;
            if (barWidth <= SCALE_BAR.preferredMaxWidthPx && barWidth >= SCALE_BAR.minWidthPx) niceValue = v;
        }
        const barWidthPx = Math.max(SCALE_BAR.minWidthPx,
            Math.min(SCALE_BAR.maxWidthPx, niceValue / micronsPerScreenPixel));

        const scaleText = niceValue >= 1000 ? `${niceValue / 1000} mm` : `${niceValue} μm`;
        const scaleBarWidth = Math.round(barWidthPx);
        if (this._scaleText === scaleText && this._scaleBarWidth === scaleBarWidth) return;
        this._scaleText = scaleText;
        this._scaleBarWidth = scaleBarWidth;
        this.updateState({scaleText, scaleBarWidth});
    };

    // ------------------------------------------------------------------ tiled view and seen area

    /** Returns {tiledConfig, tiledZoom} when the current view shows tiled fields, else null. */
    getTiledView = () => {
        const tiledConfig = getTiledOverlayConfig(this.props.slide);
        if (!tiledConfig) return null;
        const viewLevel = Math.round(this.getZoomLevel());
        if (viewLevel <= tiledConfig.useTiledAboveLevel) return null;
        // Virtual digital zoom upscales the level below the top, for camera fields as for stitched tiles.
        const tiledZoom = getTiledZoom(tiledConfig, Math.min(viewLevel, this._servedLevelCap ?? viewLevel));
        return tiledZoom == null ? null : {tiledConfig, tiledZoom};
    };

    /** Level shown at zoom index `index`, as {kind, level} in the seen-area grid's numbering. */
    seenAreaLevelAt = (index, tiledConfig = getTiledOverlayConfig(this.props.slide)) => {
        const served = Math.min(index, this._servedLevelCap ?? index);
        if (tiledConfig && index > tiledConfig.useTiledAboveLevel) {
            const tiledZoom = getTiledZoom(tiledConfig, served);
            return tiledZoom == null ? null : {kind: TILED, level: tiledZoom};
        }
        const level = getZoomLevels(this.props.slide)[served];
        return level == null ? null : {kind: STITCHED, level};
    };

    /** H5: the level actually on screen for the current magnification. */
    getSeenAreaViewLevel = () => this.seenAreaLevelAt(Math.round(this.getZoomLevel()));

    /** Levels the zoom bounds can reach, each with the shown magnification of its first zoom stop. */
    getSeenAreaLevels = () => {
        const {slide} = this.props;
        const {min, max} = this.getZoomBounds();
        const zoomScale = getMappedZoomScale(slide, getZoomLevels(slide), this.getOsdViewerConfig());
        const tiledConfig = getTiledOverlayConfig(slide);
        const levels = new Map();
        for (let index = Math.ceil(min); index <= Math.floor(max); index++) {
            const view = this.seenAreaLevelAt(index, tiledConfig);
            const key = view && levelKey(view.kind, view.level);
            if (view && !levels.has(key)) levels.set(key, {...view, label: zoomScale?.[index]});
        }
        return [...levels.values()];
    };

    canViewSeenArea = () => !!this.getOsdViewerConfig().permissions?.can_view_seen_heatmap;

    startSeenArea = () => {
        this.stopSeenArea();
        this.seenArea = new SeenAreaController(this);
        this.seenArea.start();
        this.setState({seenArea: this.seenArea});
    };

    stopSeenArea = () => {
        if (!this.seenArea) return;
        this.seenArea.dispose();
        this.seenArea = null;
        if (!this._unmounting) this.setState({seenArea: null});
    };

    // ------------------------------------------------------------------ keyboard

    /**
     * Same keys as the OL viewer (Z toggles min/max zoom, R rotates to the next quarter turn), plus
     * G (fit to screen), the Q/W/E/T magnification presets and H (seen-area heatmap, tap or hold).
     * Only the active map reacts, as OL only receives keys on the focused map.
     */
    keyDownListener = (event) => {
        if (!this.viewer?.viewport || !isPermitted(event) || !this.isActiveMap()) return;
        if (isSeenAreaKey(event.code, store.getState().morpheusSettingReducer?.keyboard_shortcuts)) {
            if (this.canViewSeenArea()) this.seenArea?.keyDown(event.repeat);
            return;
        }
        const presetKey = getPresetKey(event.code);
        if (presetKey) {
            this.zoomToMagnification(getZoomPresets(store.getState().morpheusSettingReducer?.keyboard_shortcuts)[presetKey]);
            return;
        }
        switch (event.code) {
            case 'KeyG':
                this.viewer.viewport.goHome();
                break;
            case 'KeyZ': {
                const z = this.getZoomLevel();
                this.setZoomLevel(z * 2 > this.getMaxZoom() ? this.getMinZoom() : this.getMaxZoom());
                break;
            }
            case 'KeyR': {
                const rotation = this.viewer.viewport.getRotation() || 0;
                this.viewer.viewport.setRotation((Math.floor(rotation / 90) + 1) % 4 * 90, true);
                break;
            }
            default:
                break;
        }
    };

    /** Not gated: a release must always end a hold, even if focus or the active map changed meanwhile. */
    keyUpListener = (event) => {
        if (isSeenAreaKey(event.code, store.getState().morpheusSettingReducer?.keyboard_shortcuts)) this.seenArea?.keyUp();
    };

    onWindowBlur = () => this.seenArea?.cancelKey();

    render() {
        const {slide, height, width, displaySetting, mapsState, showBorder} = this.props;
        if (!slide) return null;

        const borderStyle = showBorder && {borderStyle: 'solid', borderColor: 'red'};
        const {showNavigator} = this.state;
        const gripStyle = showNavigator ? this._getNavigatorResizeHandleStyle() : null;

        // The grip is a sibling of the OSD container so it stacks above the navigator canvas.
        return (
            <div ref={this.viewerShellRef} style={{position: 'relative', height, width}}>
                <div
                    id={`slide-map-${this.props.id}`}
                    ref={this.viewerRef}
                    style={{
                        height: '100%',
                        width: '100%',
                        backgroundColor: colorArrayToRGB(slide.background_color?.split(',').map(Number)),
                        filter: convertSettingsToFilter(displaySetting),
                        position: 'relative',
                        ...borderStyle,
                    }}
                >
                    {mapsState && Object.keys(mapsState).length > 1 && (
                        <Paper sx={{position: 'absolute', zIndex: 2, right: 0, top: 0, borderRadius: 0, paddingInlineStart: 4}}>
                            <SlideName {...this.props}/>
                        </Paper>
                    )}

                    <Tooltip title={showNavigator ? 'Hide minimap' : 'Show minimap'} placement="left">
                        <IconButton
                            data-html2canvas-ignore
                            onClick={this.toggleNavigator}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: NAVIGATOR_LAYOUT.toggleTop,
                                right: NAVIGATOR_LAYOUT.toggleRight,
                                zIndex: 200,
                                backgroundColor: 'rgba(255,255,255,0.95)',
                                border: '2px solid #333',
                                borderRadius: '6px',
                                padding: '4px',
                                '&:hover': {backgroundColor: 'rgba(230,230,230,0.95)'},
                            }}
                        >
                            {showNavigator
                                ? <MapIcon sx={{fontSize: 18, color: '#333'}}/>
                                : <MapOutlinedIcon sx={{fontSize: 18, color: '#333'}}/>}
                        </IconButton>
                    </Tooltip>

                    <div
                        data-html2canvas-ignore
                        style={{
                            position: 'absolute',
                            top: NAVIGATOR_LAYOUT.toggleTop,
                            right: NAVIGATOR_LAYOUT.toggleRight + 36,
                            zIndex: 200,
                            display: 'flex',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <SeenAreaControls controller={this.state.seenArea}/>
                        <BuildManifestButton mapId={this.props.id} slide={slide}/>
                    </div>

                    <img className="morphle-watermark" id={componentIds.morphleWatermark} alt="logo" src={MorphleLogo}/>
                </div>

                {showNavigator && gripStyle && (
                    <div
                        className="osd-navigator-resize-handle"
                        data-html2canvas-ignore
                        role="separator"
                        aria-label="Resize navigator"
                        title="Drag to resize navigator"
                        onPointerDown={this._onNavigatorResizeStart}
                        style={{position: 'absolute', zIndex: 10000, cursor: 'nesw-resize', touchAction: 'none', ...gripStyle}}
                    />
                )}
            </div>
        );
    }
}

export default BaseSlideMapOSD;
